import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { 
  TreePine, Check, ArrowRight, Building2, Globe, Users,
  Leaf, Shield
} from 'lucide-react';

const PublicPageEN = () => {
  return (
    <div data-testid="public-page-en" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
              <TreePine className="w-4 h-4 text-slate-900" />
              <span className="text-sm font-semibold text-slate-900">Public Infrastructure</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-6">
              Sustainable Mobility for Communities
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Install chargers in public spaces and support the transition to clean transport. 
              Ideal for municipalities, government organizations, and public institutions.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/en/contact">
                  Request Information
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* For Who Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              Ideal for Public Sector
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: Building2,
                title: 'Municipalities', 
                desc: 'Deploy charging infrastructure in city centers, parking lots, and public facilities'
              },
              { 
                icon: Globe,
                title: 'Government Institutions', 
                desc: 'Lead by example with EV charging at government buildings and offices'
              },
              { 
                icon: Users,
                title: 'Public Organizations', 
                desc: 'Universities, hospitals, and public institutions serving large populations'
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-3xl p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-['Inter_Tight'] mb-2">{item.title}</h3>
                <p className="text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                alt="Public charging station"
                className="rounded-3xl shadow-xl"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
                Benefits for Your Community
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: Leaf, title: 'Environmental Impact', desc: 'Reduce carbon emissions and improve air quality in your community' },
                  { icon: Users, title: 'Public Service', desc: 'Provide essential infrastructure for the growing number of EV owners' },
                  { icon: Globe, title: 'Future-Ready', desc: 'Position your municipality as a leader in sustainable development' },
                  { icon: Shield, title: 'Reliable Infrastructure', desc: '24/7 operation with professional maintenance and support' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-lime-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Partnership Model */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
              Flexible Partnership Models
            </h2>
            <p className="text-slate-500 mb-8 leading-relaxed">
              We offer various partnership models tailored to your organization's needs and budget. 
              From full ownership to revenue-sharing arrangements.
            </p>
            
            <ul className="space-y-4 text-left max-w-xl mx-auto">
              {[
                'Full installation and maintenance by AlbMove',
                'Flexible financing and ownership options',
                'Revenue sharing opportunities',
                'Grant and funding application support',
                'Long-term operation agreements'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-slate-900" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Inter_Tight'] mb-4">
              Build Sustainable Infrastructure
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Contact us to discuss how we can help your community transition to electric mobility.
            </p>
            <Button 
              asChild
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/en/contact">
                Start the Conversation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicPageEN;
